










































































































import { Vue, Component, Prop } from 'vue-property-decorator';

import { Surface, Typography } from '@/components/atoms';
import CustomersIcon from '@/components/icons/Customers.vue';
import CalendarIcon from '@/components/icons/Calendar.vue';
import TogglesIcon from '@/components/icons/Toggles.vue';
import InfoIcon from '@/components/icons/Info.vue';
import CircleBlock from '@/components/icons/CircleBlock.vue';
import CoinsIcon from '@/components/icons/Coins.vue';
import CircleCancel from '@/components/icons/CircleCancel.vue';
import PlusCircleIcon from '@/components/icons/PlusCircle.vue';
import SpecialCoinIcon from '@/components/icons/SpecialCoin.vue';
import ClockIcon from '@/components/icons/Clock.vue';
import {
  EOfferRulesName,
  ESpecialOfferRules,
  IOfferMethod,
  SpecialOfferTypeEnum,
  getRedeemableString,
  getRenewedValue
} from '@/models/offer';
import { isEveryone } from '@/models/offerAudience';

import DateUtils from '@/utils/DateUtils';
import { renderTextWithLinks } from '@/utils/renderTextWithLinks';
import { getUrlHostname } from '@/utils/getUrlHostname';

import flagKeys from '@/constants/flagKeys';
import { ILocation } from '@/models/location';
import { getLabelInfo } from '@/utils/getLabelInfo';
import eventBus from '@/utils/eventBus';
import { ModalOptionsV2 } from '@/components/Modal/ModalOptions';
import InStoreIcon from '@/components/icons/InStore.vue';

@Component({
  components: {
    Surface,
    Typography,
    CustomersIcon,
    CalendarIcon,
    TogglesIcon,
    InfoIcon,
    PlusCircleIcon,
    SpecialCoinIcon,
    CircleBlock,
    CoinsIcon,
    CircleCancel,
    ClockIcon,
    InStoreIcon
  }
})
export default class CoinSettingsOverviewV2 extends Vue {
  @Prop() offer!: Record<string, any>;
  @Prop({ default: () => [] }) locations?: Array<ILocation>;
  @Prop() offerLink?: string;

  get tPrefix() {
    return 'offers.form';
  }

  // Only the creation form sends `audienceOverview`; elsewhere the line stays hidden.
  get audienceLabel() {
    const overview = this.offer && this.offer.audienceOverview;
    if (!overview) return '';
    const prefix = `${this.tPrefix}.audience`;
    if (overview.locked || isEveryone(overview.selection))
      return this.$t(`${prefix}.overview.everyone`);
    const labels = overview.selection.statuses.map((status: string) =>
      this.$t(`${prefix}.statuses.${status}.title`)
    );
    if (overview.selection.nonMembers)
      labels.push(this.$t(`${prefix}.nonMembers.title`));
    return labels.length
      ? labels.join(' • ')
      : this.$t(`${prefix}.overview.empty`);
  }

  get rules() {
    return this.offer.rulesOverview;
  }

  get isHappyHour() {
    return this.offer.specialType === SpecialOfferTypeEnum.HAPPY_HOUR;
  }

  get flags() {
    return this.$store.getters['flags/getFlags'] || [];
  }

  get restrictionFieldIsActivated() {
    return this.flags.includes(flagKeys.offerRestrictionField);
  }

  get coinsLabel() {
    const coinLimit = this.rules[ESpecialOfferRules.LIMIT_RULE];
    if (!coinLimit || !coinLimit.value || !coinLimit.enabled)
      return this.$t(`${this.tPrefix}.coinRule.overviewEmpty`);

    return this.$t(`${this.tPrefix}.coinRule.overview`, {
      coinLimit: coinLimit.value,
      coin: this.$tc('common.pluralize.offer', coinLimit.value)
    });
  }

  get showRenewed() {
    if (
      !this.rules[ESpecialOfferRules.RENEWED_RULE] ||
      !this.rules[ESpecialOfferRules.LIMIT_RULE]
    )
      return false;
    return (
      this.rules[ESpecialOfferRules.RENEWED_RULE].enabled &&
      this.rules[ESpecialOfferRules.LIMIT_RULE].enabled &&
      getRenewedValue(this.rules[ESpecialOfferRules.RENEWED_RULE].value).time
    );
  }

  get renewedLabel() {
    const renewedLimit = getRenewedValue(
      this.rules[ESpecialOfferRules.RENEWED_RULE].value
    );

    const timeNum = Number(renewedLimit.time);
    if (timeNum === 1) {
      return this.$t(`${this.tPrefix}.renewedRule.overviewOne`, {
        period: renewedLimit.periodFormatted
      });
    }

    return this.$t(`${this.tPrefix}.renewedRule.overview`, {
      period: renewedLimit.periodFormatted,
      time: renewedLimit.time
    });
  }

  get redeemableTimeLabel() {
    return getRedeemableString(
      this.rules[EOfferRulesName.REDEEMABLE_DAY_RULE].value || '',
      this.rules[EOfferRulesName.REDEEMABLE_HOUR_RULE].value || ''
    );
  }

  get customersLabel() {
    const customerLimit = this.rules[ESpecialOfferRules.SUPPLY_RULE];
    if (!customerLimit || !customerLimit.value || !customerLimit.enabled)
      return this.$t(`${this.tPrefix}.customerRule.overviewEmpty`);

    return this.$t(`${this.tPrefix}.customerRule.overview`, {
      customerLimit: customerLimit.value,
      customer: this.$tc('common.pluralize.member', customerLimit.value)
    });
  }

  get expirationLabel() {
    const expiration = this.rules[ESpecialOfferRules.EXPIRATION_RULE];
    if (!expiration || !expiration.value || !expiration.enabled)
      return this.$t(`${this.tPrefix}.expirationRule.overviewEmpty`);

    return this.$t(`${this.tPrefix}.expirationRule.overview`, {
      date: DateUtils.formattedDate({ date: expiration.value })
    });
  }

  get combineLabel() {
    const expiration = this.rules[ESpecialOfferRules.COMBINED_RULE];
    if (!expiration || !expiration.enabled)
      return this.$t(`${this.tPrefix}.combineRule.overviewEmpty`);

    return this.$t(`${this.tPrefix}.combineRule.overview`);
  }

  get restrictionsLabel() {
    if (!this.offer || !this.offer.restrictions) return [];

    const label = this.offer.restrictions;

    const openInNewTab = true;

    return renderTextWithLinks(label, openInNewTab).split(';');
  }

  get businessInfo() {
    return this.$store.getters['business/getBusinessInfo'].business;
  }

  get redeemableInOtherLocationsLabel() {
    const [, ...otherLocations] = this.locations || [];

    if (!otherLocations.length || this.offer.method === IOfferMethod.ONLINE)
      return '';

    return `+ ${otherLocations.length} ${this.$tc(
      'common.pluralize.location',
      otherLocations.length
    )}`;
  }

  get hasLocations() {
    return this.locations && this.locations.length;
  }

  get redeemableLabel() {
    const locations = this.locations || [];

    const [firstLocation] = locations;

    const website =
      [IOfferMethod.BOTH, IOfferMethod.ONLINE].includes(this.offer.method) &&
      this.redeemLink
        ? this.redeemLink
        : '';

    let label =
      (firstLocation && firstLocation.name) ||
      getLabelInfo(['address', 'city'], firstLocation);

    const labelMapper: Record<IOfferMethod, string> = {
      [IOfferMethod.BOTH]: website
        ? `${this.$t(`${this.tPrefix}.otherRules.redeemableOnlineAndInStore`, {
            online: website,
            inStore: label
          })} `
        : `${this.$t(
            `${this.tPrefix}.otherRules.redeemableInStore`
          )} at ${label}`,
      [IOfferMethod.INSTORE]: this.hasLocations
        ? `${this.$t(
            `${this.tPrefix}.otherRules.redeemableInStore`
          )} at ${label}`
        : String(this.$t(`${this.tPrefix}.otherRules.redeemableInStore`)),
      [IOfferMethod.ONLINE]: website
        ? `${this.$t(
            `${this.tPrefix}.otherRules.redeemableOnline`
          )} at ${website}`
        : String(this.$t(`${this.tPrefix}.otherRules.redeemableOnline`))
    };

    if (!labelMapper[this.offer.method as IOfferMethod]) return '';

    return labelMapper[this.offer.method as IOfferMethod];
  }

  get redeemLink() {
    if (this.businessInfo.website === this.offerLink) {
      return getUrlHostname(this.offerLink);
    }

    return this.offerLink;
  }

  handleOpenLocationsGroupModal() {
    eventBus.$emit(
      'openSubModal',
      new ModalOptionsV2({
        icon: 'none',
        hideCancelButton: true,
        confirmEvent: '',
        componentToRender: 'locationsGroupedByCityModal'
      }),
      {
        title: 'Redeem locations',
        locations:
          this.offer.method !== IOfferMethod.ONLINE ? this.locations : [],
        ...(this.offer.method === IOfferMethod.BOTH &&
          this.redeemLink && {
            website: this.redeemLink
          })
      }
    );
  }
}
